import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Pagination,
  Progress,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronRight,
  IconCircleCheck,
  IconClock,
  IconCloudUpload,
  IconExternalLink,
  IconScan,
  IconX,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Page from '../../../../components/Page/Page';
import { useAuth } from '../../../../contexts/AuthProvider';
import { useI18n } from '../../../../contexts/I18nProvider';
import useCompany from '../../../../hooks/useCompany';
import useJobSse from '../../../../hooks/useJobSse';
import PayslipOcrService from '../../../../services/PayslipOcrService';
import {
  JobProgress,
  jobStepCounter,
} from '../../../../types/api/response/job';
import {
  OcrPayslipJob,
  OcrPayslipJobItem,
  OcrPayslipJobItemStatus,
  OcrPayslipJobStatus,
} from '../../../../types/types';
import { toDateOfDayString } from '../../../../utils/format';
import handleErrorMessage from '../../../../utils/handleErrorMessage';
import s from './PayslipOcr.module.sass';
import PayslipOcrReview from './PayslipOcrReview';

const ITEMS_PAGE_SIZE = 10;

const MAX_SIZE_BYTES = 50 * 1024 * 1024;
const MAX_SIZE_LABEL = '50 Mo';

const TERMINAL_STATUSES: OcrPayslipJobStatus[] = [
  OcrPayslipJobStatus.DONE,
  OcrPayslipJobStatus.PARTIAL,
  OcrPayslipJobStatus.FAILED,
];

/** Dérive le statut OCR métier depuis l'event unifié (PARTIAL = succès avec des items en échec). */
function deriveOcrStatus(
  jp: JobProgress,
  total: number,
  done: number,
  failed: number
): OcrPayslipJobStatus {
  if (jp.state === 'ERROR') return OcrPayslipJobStatus.FAILED;
  if (jp.state === 'SUCCESS')
    return failed > 0 ? OcrPayslipJobStatus.PARTIAL : OcrPayslipJobStatus.DONE;
  return total === 0 && done === 0
    ? OcrPayslipJobStatus.PENDING
    : OcrPayslipJobStatus.RUNNING;
}

/**
 * Construit un {@link OcrPayslipJob} partiel à partir d'un {@link JobProgress}. Les champs non
 * transmis par le SSE light sont récupérés de l'entrée déjà connue si elle existe (cas typique :
 * refresh → `fallbackFetch` a chargé le job complet, puis les events SSE le tiennent à jour).
 * Les items[] sont synthétisés depuis les compteurs pour que la barre de progression avance.
 */
function jobProgressToOcrJob(
  jp: JobProgress,
  existing?: OcrPayslipJob
): OcrPayslipJob {
  const step = jp.steps?.[0];
  const total = jobStepCounter(step, 'total');
  const done = jobStepCounter(step, 'done');
  const failed = jobStepCounter(step, 'failed');
  const synthItems: OcrPayslipJobItem[] = Array.from(
    { length: total },
    (_, i) => {
      const prev = existing?.items?.[i];
      if (prev && prev.status !== OcrPayslipJobItemStatus.PENDING) return prev;
      let status: OcrPayslipJobItemStatus = OcrPayslipJobItemStatus.PENDING;
      if (i < failed) status = OcrPayslipJobItemStatus.FAILED;
      else if (i < done) status = OcrPayslipJobItemStatus.OK;
      return prev ? { ...prev, status } : { sourceBucketKey: '', status };
    }
  );
  return {
    id: jp.jobId,
    companyId: jp.companyId ?? existing?.companyId ?? '',
    requestedBy: existing?.requestedBy ?? '',
    status: deriveOcrStatus(jp, total, done, failed),
    sourceBucketKeys: existing?.sourceBucketKeys ?? [],
    items: synthItems,
    createdAt: existing?.createdAt ?? new Date().toISOString(),
    startedAt: existing?.startedAt,
    completedAt: existing?.completedAt,
    lockedBy: existing?.lockedBy,
    lockedAt: existing?.lockedAt,
    errorMessage: jp.errorMessage ?? existing?.errorMessage,
  };
}

/** Inverse : full job → JobProgress (le service REST retourne un full job au `fallbackFetch`). */
function ocrJobToJobProgress(job: OcrPayslipJob): JobProgress {
  const items = job.items ?? [];
  const done = items.filter(
    (it) => it.status !== OcrPayslipJobItemStatus.PENDING
  ).length;
  const failed = items.filter(
    (it) => it.status === OcrPayslipJobItemStatus.FAILED
  ).length;
  const state: JobProgress['state'] =
    job.status === OcrPayslipJobStatus.FAILED
      ? 'ERROR'
      : job.status === OcrPayslipJobStatus.DONE ||
        job.status === OcrPayslipJobStatus.PARTIAL
      ? 'SUCCESS'
      : 'PROCESSING';
  return {
    jobId: job.id,
    companyId: job.companyId,
    jobType: 'PAYSLIP_OCR',
    state,
    currentStepIndex: 0,
    steps: [
      {
        type: 'OCR',
        state:
          state === 'SUCCESS'
            ? 'SUCCESS'
            : state === 'ERROR'
            ? 'FAILED'
            : 'RUNNING',
        counters: { total: items.length, done, failed },
      },
    ],
    errorMessage: job.errorMessage,
  };
}

function upsertJob(
  jobs: OcrPayslipJob[],
  fresh: OcrPayslipJob
): OcrPayslipJob[] {
  const idx = jobs.findIndex((j) => j.id === fresh.id);
  if (idx < 0) return [fresh, ...jobs];
  const next = [...jobs];
  next[idx] = fresh;
  return next;
}

function shortJobId(id: string | null | undefined): string {
  if (!id || id.length <= 7) return id ?? '';
  return `${id.slice(0, 3)}…${id.slice(-3)}`;
}

function jobFilename(job: OcrPayslipJob | null | undefined): string {
  const key = job?.sourceBucketKeys?.[0];
  if (!key) return '—';
  // Format S3 : « ocr/{jobId}/{idx}-{normalizedFilename} » (cf. StartPayslipOcrBatchUseCase).
  const tail = key.split('/').pop() ?? '';
  return tail.replace(/^\d+-/, '');
}

function jobProgressPercent(job: OcrPayslipJob): number {
  const total = job.items?.length ?? 0;
  if (total === 0) return job.status === OcrPayslipJobStatus.PENDING ? 0 : 5;
  const done = job.items.filter(
    (it) => it.status !== OcrPayslipJobItemStatus.PENDING
  ).length;
  return Math.round((done / total) * 100);
}

function doneItemsCount(job: OcrPayslipJob): number {
  if (!job.items) return 0;
  return job.items.filter((it) => it.status !== OcrPayslipJobItemStatus.PENDING)
    .length;
}

function statusColor(status: OcrPayslipJobStatus): string {
  switch (status) {
    case OcrPayslipJobStatus.DONE:
      return 'green';
    case OcrPayslipJobStatus.PARTIAL:
      return 'orange';
    case OcrPayslipJobStatus.FAILED:
      return 'red';
    case OcrPayslipJobStatus.RUNNING:
      return 'blue';
    default:
      return 'gray';
  }
}

/**
 * Mois de paie du premier bulletin lu (« 01/09/2026 » ou « 01-09-2026 », comme le back qui y
 * range le bulletin), ou null si aucune période n'a été extraite.
 */
function importedMonth(job: OcrPayslipJob): Date | null {
  for (const item of job.items) {
    const m = item.extractedFields?.payPeriodStart?.match(
      /^\d{1,2}[/-](\d{1,2})[/-](\d{4})$/
    );
    if (m) return new Date(Number(m[2]), Number(m[1]) - 1, 1);
  }
  return null;
}

type Props = {
  /** Entreprise cliente ciblée quand l'écran est embarqué dans la vue gestionnaire de paie. */
  customerCompanyId?: string;
  /**
   * Rendu sans le shell `Page` : l'écran est imbriqué dans « Bulletins de paie » au lieu d'être
   * servi par sa route dédiée. La vérification d'un bulletin s'affiche alors en place, et le job
   * courant n'est pas synchronisé avec l'URL — la route hôte n'est pas celle de l'OCR.
   */
  inline?: boolean;
  /** Appelé à la fin d'un job avec le mois des bulletins extraits, où ils ont été rangés. */
  onImported?: (month: Date) => void;
};

export default function PayslipOcr({
  customerCompanyId,
  inline = false,
  onImported,
}: Props) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { id: companyId, company } = useCompany(user, customerCompanyId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [previousJobs, setPreviousJobs] = useState<OcrPayslipJob[]>([]);
  const [uploading, setUploading] = useState(false);
  // currentJobId est synchronisé avec le query param `?ocrJobId=` : un reload navigateur pendant
  // un traitement rétablit l'état (le `fallbackFetch` du useJobSse repeuple depuis Mongo, puis la
  // SSE reprend si le job n'est pas terminal).
  const [currentJobId, setCurrentJobId] = useState<string | null>(
    inline ? null : searchParams.get('ocrJobId')
  );
  const [reviewedItemIdx, setReviewedItemIdx] = useState<number | null>(null);
  const [itemsPage, setItemsPage] = useState(1);

  useEffect(() => {
    if (inline) return;
    const next = new URLSearchParams(searchParams);
    if (currentJobId) {
      if (next.get('ocrJobId') === currentJobId) return;
      next.set('ocrJobId', currentJobId);
    } else {
      if (!next.has('ocrJobId')) return;
      next.delete('ocrJobId');
    }
    setSearchParams(next, { replace: true });
  }, [currentJobId, searchParams, setSearchParams, inline]);

  const refreshPrevious = useCallback(async () => {
    if (!companyId) return;
    try {
      const jobs = await PayslipOcrService.listJobs(companyId);
      const sorted = [...jobs].sort((a, b) => {
        const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return db - da;
      });
      setPreviousJobs(sorted);
      setCurrentJobId((prev) => prev ?? sorted[0]?.id ?? null);
    } catch (error) {
      console.error('OCR jobs list failed', error);
      showNotification({
        id: 'error-listOcrJobs',
        title: t('w.error'),
        message: handleErrorMessage(error, t),
        color: 'red',
        icon: <IconX />,
      });
    }
  }, [companyId]);

  useEffect(() => {
    void refreshPrevious();
  }, [refreshPrevious]);

  // ──────────────────────────── Suivi du job courant via SSE ────────────────────────────
  // Le SSE pousse le modèle unifié JobProgress (compteurs uniquement, pas d'items détaillés).
  // On stocke `previousJobs` en OcrPayslipJob[] pour rendre la table finale ; l'event est traduit
  // en OcrPayslipJob partiel pour faire avancer la barre, et `onTerminal` refetch le job complet.
  const sseUrl = useMemo(
    () =>
      currentJobId && companyId
        ? PayslipOcrService.buildJobSseUrl(companyId, currentJobId)
        : null,
    [companyId, currentJobId]
  );

  const { job: liveJob } = useJobSse<JobProgress>(sseUrl, {
    isTerminal: (jp) => jp.state === 'SUCCESS' || jp.state === 'ERROR',
    fallbackFetch: async () => {
      if (!currentJobId || !companyId) return null;
      const full = await PayslipOcrService.getJob(companyId, currentJobId);
      return full ? ocrJobToJobProgress(full) : null;
    },
    onTerminal: async () => {
      try {
        if (currentJobId && companyId) {
          const full = await PayslipOcrService.getJob(companyId, currentJobId);
          if (full) {
            setPreviousJobs((prev) => upsertJob(prev, full));
            const month = importedMonth(full);
            if (month) onImported?.(month);
          }
        }
      } catch {
        // Si le GET échoue, on garde la version partielle synthétisée depuis le progress event
        // (statut correct, items synthétiques) — la table reste vide mais la barre est juste.
      }
      // Resync : « Bulletins de paie » réaffichera les bonnes valeurs sans F5 manuel.
      queryClient.invalidateQueries({
        queryKey: ['PayslipService.getPayslipsMonthInfo'],
      });
    },
  });

  useEffect(() => {
    if (!liveJob) return;
    setPreviousJobs((prev) =>
      upsertJob(
        prev,
        jobProgressToOcrJob(
          liveJob,
          prev.find((j) => j.id === liveJob.jobId)
        )
      )
    );
  }, [liveJob]);

  const currentJob = useMemo(
    () => previousJobs.find((j) => j.id === currentJobId) ?? null,
    [previousJobs, currentJobId]
  );

  // ──────────────────────────── Pagination des items ────────────────────────────
  // Un lot d'un fichier multi-BS monte à plusieurs dizaines de bulletins : sans pagination, la
  // table repousse la section « Résultat d'import » hors de l'écran. L'index absolu est conservé
  // à côté de chaque item, c'est lui qui identifie l'item côté back (`items/{idx}`).

  const itemsTotal = currentJob?.items?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(itemsTotal / ITEMS_PAGE_SIZE));

  useEffect(() => {
    setItemsPage(1);
  }, [currentJobId]);

  // Une page devenue vide (job plus court après refetch) ramène sur la dernière page existante.
  useEffect(() => {
    setItemsPage((page) => Math.min(page, pageCount));
  }, [pageCount]);

  const itemsRangeFrom = (itemsPage - 1) * ITEMS_PAGE_SIZE + 1;
  const itemsRangeTo = Math.min(itemsPage * ITEMS_PAGE_SIZE, itemsTotal);

  const pagedItems = useMemo(
    () =>
      (currentJob?.items ?? [])
        .map((item, index) => ({ item, index }))
        .slice(itemsRangeFrom - 1, itemsRangeTo),
    [currentJob, itemsRangeFrom, itemsRangeTo]
  );

  // ──────────────────────────── Upload ────────────────────────────

  async function handleDrop(files: FileWithPath[]) {
    const file = files[0];
    if (!file || !companyId) return;
    setUploading(true);
    try {
      const { jobId } = await PayslipOcrService.uploadBatch(companyId, file);
      showNotification({
        id: 'success-uploadOcrBatch',
        message: t('payslipOcr.upload.success'),
        color: 'green',
        icon: <IconCheck />,
      });
      setCurrentJobId(jobId);
      await refreshPrevious();
    } catch (error) {
      console.error('OCR upload failed', error);
      showNotification({
        id: 'error-uploadOcrBatch',
        title: t('payslipOcr.upload.error'),
        message: handleErrorMessage(error, t),
        color: 'red',
        icon: <IconX />,
      });
    } finally {
      setUploading(false);
    }
  }

  function handleReject() {
    showNotification({
      id: 'error-rejectOcrFile',
      title: t('w.error'),
      message: t('payslipOcr.upload.rejected', MAX_SIZE_LABEL),
      color: 'red',
      icon: <IconX />,
    });
  }

  // ──────────────────────────── Helpers de rendu ────────────────────────────

  function formatTime(iso: string | null | undefined): string {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleTimeString(lang, {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }

  function formatFullDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const date = d.toLocaleDateString(lang, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      const time = d.toLocaleTimeString(lang, {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${date} · ${time}`;
    } catch {
      return iso;
    }
  }

  function statusLabel(status: OcrPayslipJobStatus): string {
    return t(`payslipOcr.status.${status.toLowerCase()}`);
  }

  function panelLabel(job: OcrPayslipJob): string {
    switch (job.status) {
      case OcrPayslipJobStatus.DONE:
      case OcrPayslipJobStatus.PARTIAL:
      case OcrPayslipJobStatus.FAILED:
        return t('payslipOcr.panel.labelDone');
      case OcrPayslipJobStatus.PENDING:
        return t('payslipOcr.panel.labelPending');
      default:
        return t('payslipOcr.panel.labelRunning');
    }
  }

  function itemName(item: OcrPayslipJobItem, idx: number): string {
    if (item.extractedFields?.lastName) {
      return `${item.extractedFields.lastName} ${
        item.extractedFields.firstName ?? ''
      }`.trim();
    }
    return t('payslipOcr.progress.unnamedItem', idx + 1);
  }

  function renderItemIcon(status: OcrPayslipJobItemStatus) {
    switch (status) {
      case OcrPayslipJobItemStatus.OK:
        return (
          <ThemeIcon variant="white" color="green" size="sm">
            <IconCircleCheck />
          </ThemeIcon>
        );
      case OcrPayslipJobItemStatus.FAILED:
        return (
          <ThemeIcon variant="white" color="red" size="sm">
            <IconAlertTriangle />
          </ThemeIcon>
        );
      default:
        return (
          <ThemeIcon variant="white" color="gray" size="sm">
            <IconClock />
          </ThemeIcon>
        );
    }
  }

  function openReview(idx: number) {
    if (!currentJobId) return;
    if (inline) {
      setReviewedItemIdx(idx);
      return;
    }
    navigate(`/end-of-month/payslips/ocr/${currentJobId}/items/${idx}`);
  }

  const hasTerminalStatus =
    currentJob !== null && TERMINAL_STATUSES.indexOf(currentJob.status) >= 0;

  if (inline && currentJobId && reviewedItemIdx !== null) {
    return (
      <PayslipOcrReview
        customerCompanyId={customerCompanyId}
        inline
        jobId={currentJobId}
        itemIndex={reviewedItemIdx}
        onBack={() => {
          setReviewedItemIdx(null);
          void refreshPrevious();
        }}
      />
    );
  }

  const content = (
    <div className={inline ? `${s.layout} ${s.layoutInline}` : s.layout}>
      {/* ─────────── Sidebar ─────────── */}
      <Card withBorder radius="md" p="md" className={s.sidebar}>
        <Dropzone
          onDrop={handleDrop}
          onReject={handleReject}
          accept={[MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg]}
          maxSize={MAX_SIZE_BYTES}
          maxFiles={1}
          loading={uploading}
          classNames={{ root: s.dropzoneRoot }}
        >
          <Group justify="center" gap="xs">
            <IconCloudUpload size={20} />
            <Text size="sm">{t('payslipOcr.upload.compactLabel')}</Text>
          </Group>
        </Dropzone>

        <Group justify="space-between" mt="lg" mb="sm">
          <Title order={4}>{t('payslipOcr.history.titleShort')}</Title>
          <Text size="xs" c="dimmed">
            {t('payslipOcr.history.count', previousJobs.length)}
          </Text>
        </Group>

        {previousJobs.length === 0 ? (
          <Text size="sm" c="dimmed" p="sm">
            {t('payslipOcr.history.empty')}
          </Text>
        ) : (
          <div className={s.jobList}>
            {previousJobs.map((j) => (
              <div
                key={j.id}
                role="button"
                tabIndex={0}
                className={`${s.jobItem} ${
                  j.id === currentJobId ? s.jobItemActive : ''
                }`}
                onClick={() => setCurrentJobId(j.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setCurrentJobId(j.id);
                  }
                }}
              >
                <span
                  className={s.jobItemDot}
                  style={{
                    backgroundColor: `var(--mantine-color-${statusColor(
                      j.status
                    )}-6)`,
                  }}
                />
                <div className={s.jobItemMain}>
                  <Text size="sm">
                    <span className={s.jobItemId}>{shortJobId(j.id)}</span>
                    <Text span c="dimmed">
                      {' '}
                      · {formatTime(j.createdAt)}
                    </Text>
                  </Text>
                  <Text size="xs" c="dimmed" truncate title={jobFilename(j)}>
                    {jobFilename(j)}
                  </Text>
                </div>
                <Stack gap={0} align="flex-end">
                  <Text size="sm" fw={500}>
                    {j.items?.length ?? 0}
                  </Text>
                  <Text size="10px" c="dimmed">
                    BS
                  </Text>
                </Stack>
                {j.id === currentJobId &&
                  (j.status === OcrPayslipJobStatus.RUNNING ||
                    j.status === OcrPayslipJobStatus.PENDING) && (
                    <Progress
                      className={s.jobItemProgress}
                      size="xs"
                      value={jobProgressPercent(j)}
                      animated={j.status === OcrPayslipJobStatus.PENDING}
                    />
                  )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ─────────── Panneau de détail ─────────── */}
      {currentJob ? (
        <Card withBorder radius="md" p="lg" className={s.detail}>
          <Group justify="space-between" align="flex-start" mb="lg">
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                {panelLabel(currentJob)}
              </Text>
              <Group gap="sm">
                <Title order={3}>Job {shortJobId(currentJob.id)}</Title>
                <Badge
                  variant="light"
                  color={statusColor(currentJob.status)}
                  radius="sm"
                >
                  {statusLabel(currentJob.status)}
                </Badge>
              </Group>
            </Stack>
            {/* Inline, les listes de bulletins sont déjà sous l'import : le lien n'aurait
                  nulle part où mener. */}
            {!inline && (
              <Button
                variant="outline"
                leftSection={<IconExternalLink size={16} />}
                onClick={() => navigate('/end-of-month/payslips')}
              >
                {t('payslipOcr.panel.viewPayslips')}
              </Button>
            )}
          </Group>

          <div className={s.metaGrid}>
            <div>
              <div className={s.metaLabel}>{t('payslipOcr.panel.file')}</div>
              <Text truncate title={jobFilename(currentJob)}>
                {jobFilename(currentJob)}
              </Text>
            </div>
            <div>
              <div className={s.metaLabel}>
                {t('payslipOcr.panel.createdAt')}
              </div>
              <Text>{formatFullDate(currentJob.createdAt)}</Text>
            </div>
            <div>
              <div className={s.metaLabel}>
                {t('payslipOcr.panel.extractedCount')}
              </div>
              <Text>
                {doneItemsCount(currentJob)} / {currentJob.items?.length ?? 0}
              </Text>
            </div>
          </div>

          {(currentJob.status === OcrPayslipJobStatus.PENDING ||
            currentJob.status === OcrPayslipJobStatus.RUNNING) && (
            <Stack gap={4} mt="lg">
              <Progress
                size="lg"
                radius="xl"
                value={jobProgressPercent(currentJob)}
                animated={currentJob.status === OcrPayslipJobStatus.PENDING}
              />
              <Text size="xs" c="dimmed">
                {currentJob.status === OcrPayslipJobStatus.RUNNING
                  ? t(
                      'payslipOcr.panel.processingPercent',
                      jobProgressPercent(currentJob)
                    )
                  : t('payslipOcr.progress.pending')}
              </Text>
            </Stack>
          )}

          {currentJob.errorMessage && (
            <Alert
              mt="md"
              variant="light"
              color="red"
              icon={<IconAlertTriangle />}
            >
              {currentJob.errorMessage}
            </Alert>
          )}

          {hasTerminalStatus && pagedItems.length > 0 && (
            <>
              <Table mt="lg" highlightOnHover={false}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40} />
                    <Table.Th>{t('payslipOcr.progress.colName')}</Table.Th>
                    <Table.Th>{t('payslipOcr.progress.colEditor')}</Table.Th>
                    <Table.Th>{t('payslipOcr.progress.colPeriod')}</Table.Th>
                    <Table.Th w={40} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pagedItems.map(({ item, index }) => {
                    const clickable =
                      item.status === OcrPayslipJobItemStatus.OK;
                    return (
                      <Table.Tr
                        key={`${item.sourceBucketKey}-${index}`}
                        className={clickable ? s.clickableRow : undefined}
                        onClick={
                          clickable ? () => openReview(index) : undefined
                        }
                      >
                        <Table.Td>{renderItemIcon(item.status)}</Table.Td>
                        <Table.Td fw={500}>{itemName(item, index)}</Table.Td>
                        <Table.Td>{item.detectedEditor ?? '—'}</Table.Td>
                        <Table.Td>
                          {item.extractedFields?.payPeriodStart ?? '—'}
                        </Table.Td>
                        <Table.Td>
                          {clickable && <IconChevronRight size={16} />}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
              {pageCount > 1 && (
                <Group justify="space-between" mt="md">
                  {/* Plage purement numérique : rien à traduire, donc pas de clé i18n. */}
                  <Text size="xs" c="dimmed">
                    {itemsRangeFrom}–{itemsRangeTo} / {itemsTotal} BS
                  </Text>
                  <Pagination
                    size="sm"
                    value={itemsPage}
                    total={pageCount}
                    onChange={setItemsPage}
                  />
                </Group>
              )}
            </>
          )}
        </Card>
      ) : (
        <Card
          withBorder
          radius="md"
          p="xl"
          className={`${s.detail} ${s.detailEmpty}`}
        >
          <Stack align="center" justify="center" gap="xs" h="100%">
            {uploading ? (
              <Loader />
            ) : (
              <IconScan size={48} color="var(--mantine-color-gray-5)" />
            )}
            <Title order={4}>{t('payslipOcr.panel.emptyTitle')}</Title>
            <Text size="sm" c="dimmed" ta="center">
              {t('payslipOcr.panel.emptySubtitle')}
            </Text>
          </Stack>
        </Card>
      )}
    </div>
  );

  if (inline) {
    return content;
  }

  return (
    <Page
      title={t('payslipOcr.title')}
      parent={{
        label: company ? company.name : toDateOfDayString(),
        action: () => navigate('/end-of-month/payslips'),
      }}
    >
      {content}
    </Page>
  );
}
